import React, { useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getReinforcement, addReinforcement, updateReinforcement, clearCurrentReinforcement } from '../actions/reinforcementActions';
import { getTags } from '../actions/tagActions';
import ReinforcementForm from '../components/content-management/ReinforcementForm';
import AdminLayout from '../components/settings/AdminLayout';

import { X } from 'lucide-react';
import { isAdminRole } from '../utils/roleHelpers';

const ReinforcementEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { currentReinforcement, reinforcementLoading } = useSelector(state => state.reinforcement);
  const { user } = useSelector(state => state.auth);

  // Determine mode based on URL
  const isViewMode = location.pathname.includes('/view');
  const isEditMode = location.pathname.includes('/edit');
  const isNewMode = !id;

  const isAdmin = isAdminRole(user?.role);
  const isInstructor = user?.role === 'instructor';
  const isReadOnly = isInstructor || isViewMode;

  useEffect(() => {
    const loadData = async () => {
      try {
        // Always load tags for the form
        await dispatch(getTags());

        // If we have an ID, load the reinforcement
        if (id) {
          await dispatch(getReinforcement(id));
        }
      } catch (error) {
        console.error('Error loading reinforcement:', error);
      }
    };

    loadData();

    // Cleanup on unmount
    return () => {
      dispatch(clearCurrentReinforcement());
    };
  }, [dispatch, id]);

  const handleSubmit = async (reinforcementData) => {
    try {
      const action = id ? updateReinforcement(id, reinforcementData) : addReinforcement(reinforcementData);
      await dispatch(action);
      // Redirect back to reinforcements list
      navigate('/settings', { state: { activeTab: 'Reinforcements' } });
    } catch (error) {
      console.error('Error saving reinforcement:', error);
    }
  };

  const handleClose = () => {
    navigate('/settings', { state: { activeTab: 'Reinforcements' } });
  };

  // Show loading state
  if (reinforcementLoading && id) {
    return (
      <AdminLayout activeTab="Reinforcements">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="h-8 w-48 bg-gray-200 rounded animate-shimmer"></div>
            <button
              type="button"
              onClick={() => navigate('/settings', { state: { activeTab: 'Reinforcements' } })}
              className="text-gray-400 hover:text-[#1086a4] focus:outline-none transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Form Skeleton */}
          <div className="space-y-6">
            <div>
              <div className="h-4 w-24 bg-gray-200 rounded mb-2 animate-shimmer"></div>
              <div className="h-10 bg-gray-200 rounded animate-shimmer"></div>
            </div>
            <div>
              <div className="h-4 w-24 bg-gray-200 rounded mb-2 animate-shimmer"></div>
              <div className="h-64 bg-gray-200 rounded animate-shimmer"></div>
            </div>
            <div>
              <div className="h-4 w-24 bg-gray-200 rounded mb-2 animate-shimmer"></div>
              <div className="h-10 bg-gray-200 rounded animate-shimmer"></div>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // If trying to edit/view a reinforcement that doesn't exist
  if (id && !currentReinforcement && !reinforcementLoading) {
    return (
      <AdminLayout activeTab="Reinforcements">
        <div className="flex flex-col items-center justify-center py-12">
          <div className="text-red-500 text-xl mb-4">Reinforcement not found</div>
          <button
            onClick={() => navigate('/settings', { state: { activeTab: 'Reinforcements' } })}
            className="px-4 py-2 bg-[#1086a4] text-white rounded-lg hover:bg-[#054581] transition-colors"
          >
            Return to Reinforcements
          </button>
        </div>
      </AdminLayout>
    );
  }

  const mode = isViewMode ? 'view' : isEditMode ? 'edit' : 'new';

  return (
    <AdminLayout activeTab="Reinforcements">
      <ReinforcementForm
        mode={mode}
        reinforcement={currentReinforcement}
        onSubmit={handleSubmit}
        onClose={handleClose}
      />
    </AdminLayout>
  );
};

export default ReinforcementEditor;
